/* eslint-disable @typescript-eslint/no-explicit-any */
import { pickObject } from '@client/new/helpers/general';
import { ArrayItem, F1, SkipNull, toOption } from '@client/new/helpers/types';
import {
  GetProgramWithLiteChallengesQuery,
  GetProgramWithChallengesQuery,
} from 'generated/graphql';
import {
  Challenge,
  ChallengeEnd,
  ChallengeStart,
  LiteChallenge,
  Submission,
  SubmissionMedia,
  UserStatus,
  userStatuses,
} from './Challenges.types';
import { FieldPath } from 'react-hook-form';
import { capitalizeFirstLetter } from '../../promotions/view-model/discounts.mappers';

import { ChallengeForm } from '../view-model/ChallengeForm.schema';
import { PATHS } from '@client/paths';
import _ from 'lodash';

export type ServerProgramWithLiteChallenge = SkipNull<
  GetProgramWithLiteChallengesQuery['collection']
>;

export type ServerLiteChallenge = SkipNull<
  ArrayItem<SkipNull<ServerProgramWithLiteChallenge['challenges']>>
>;

export type ServerProgramWithChallenge = SkipNull<
  GetProgramWithChallengesQuery['collection']
>;

type ServerChallenge = SkipNull<
  ArrayItem<SkipNull<ServerProgramWithChallenge['challenges']>>
>;

type ServerMediaType = 'video/mp4' | 'image/jpeg';
type ServerMedia = SkipNull<
  ArrayItem<SkipNull<ServerSubmission['completionMedia']>>
>;

type ServerSubmission = ArrayItem<ServerChallenge['submissions']>;

const toChallengeStart = (c: ServerLiteChallenge): ChallengeStart => ({
  data: [new Date(c.registrationStart), new Date(c.registrationEnd)],
  ...pickObject(c.registrationRequirements, ['instagram', 'goal', 'photos']),
});

const toChallengeEnd = (c: ServerLiteChallenge): ChallengeEnd => ({
  data: [new Date(c.completionStart), new Date(c.completionEnd)],
  ...pickObject(c.completionRequirements, ['testimonial', 'photos']),
});

export const toSubmissionsURL =
  (p: { creatorId: number; programId: number }) => (challengeId: string) =>
    PATHS.CHALLENGE_DETAILS(
      p.creatorId,
      p.programId,
      parseInt(challengeId, 10),
    );

const getMode = (c: ServerLiteChallenge): LiteChallenge['mode'] => {
  const now = new Date();
  if (now < new Date(c.registrationStart)) return 'ready';
  if (now < new Date(c.registrationEnd)) return 'registration';
  if (now < new Date(c.completionEnd)) return 'completion';
  return 'finished';
};
export const toLiteChallenge =
  (toSubmissionsURL: F1<string, string>) =>
  (c: ServerLiteChallenge): LiteChallenge => {
    const registration = toChallengeStart(c);
    const completion = toChallengeEnd(c);

    return {
      challengeId: c.id,
      hidePhotos: c.hidePhotos,
      mode: getMode(c),
      name: c.name,
      completion,
      registration,
      submissionsCount: c.submissions.length,
      photoInstructions: c.photoInstructions || '',
      templates: (c?.templates || []).filter(Boolean) as string[],
      submissionsURL: toSubmissionsURL(c.id),
      createdAt: new Date(c.createdAt).getTime(),
    };
  };

const toMedia = (m: ServerMedia): SubmissionMedia => {
  const type: SubmissionMedia['type'] =
    (m.mimeType as ServerMediaType) === 'video/mp4' ? 'video' : 'photo';
  const url = `${m.cdn01}${type === 'photo' ? m.image : m.video}`;
  return {
    type,
    width: m.width || 0,
    height: m.height || 0,
    url,
    thumbnailURL: url,
  };
};

const toMediaList = (
  ms: ServerSubmission['completionMedia'],
): SubmissionMedia[] => {
  if (!ms) return [];
  return ms.map(toMedia);
};

const toUserStatus = (v: any): UserStatus =>
  typeof v === 'string' && userStatuses.includes(v as any)
    ? (v as UserStatus)
    : 'Inactive';
const toSubmission = (c: ServerSubmission): Submission => {
  const beforeMedia = toMediaList(c.registrationMedia);
  const afterMedia = toMediaList(c.completionMedia);
  const transformationMedia = toMediaList(c.transformationMedia);
  return {
    submissionId: c.submissionId,
    username: c.user.name || '',
    isFav: c.isShortlisted,
    userStatus: c.subscriptionInfo
      ? toUserStatus(c.subscriptionInfo?.status)
      : null,

    email: c.user.email || '',

    beforeMedia,
    afterMedia,
    transformationMedia,
    testimonial: c.completionAttributes?.testimonial || '',
    instagram: c.registrationAttributes.instagram || '',
    goal: c.registrationAttributes.goal || '',

    ...(c.registrationDate ? { beforeDate: new Date(c.registrationDate) } : {}),
    ...(c.completionDate ? { afterDate: new Date(c.completionDate) } : {}),

    repostable: Boolean(c.allowRepost),

    statusDate: c.subscriptionInfo?.expiresAt || '',
    workoutsTotal: c.userStateOnCompletion?.workoutsTotal || 0,
    workoutsCompleted: c.userStateOnCompletion?.workoutsCompleted || 0,
  };
};

export const toChallenge =
  (toSubmissionsURL: F1<string, string>) =>
  (c: ServerChallenge): Challenge => {
    const liteChallenge = toLiteChallenge(toSubmissionsURL)(c);
    const submissions = c.submissions.map(toSubmission);
    return { ...liteChallenge, submissions };
  };

const camelCaseToWords = (s: string) => {
  const result = s.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

const toToggleField = <T extends FieldPath<ChallengeForm>>(
  v: T,
  delta = {},
) => ({
  ...toOption<T>(
    v,
    camelCaseToWords(capitalizeFirstLetter(v.replace(/.*\./, ''))),
  ),
  ...delta,
});

export const submissionRequirementsToggles = (photoSectionEnabled: boolean) => [
  toToggleField('submissionRequirements.instagram'),
  toToggleField('submissionRequirements.goal'),
  toToggleField('submissionRequirements.photos', {
    tooltip: !photoSectionEnabled
      ? 'Photos cannot be set as required if Photos section visibility is off.'
      : null,
    disabled: !photoSectionEnabled,
  }),
];

export const completionRequirementsToggles = (photoSectionEnabled: boolean) => [
  toToggleField('completionRequirements.testimonial'),
  toToggleField('completionRequirements.photos', {
    tooltip: !photoSectionEnabled
      ? 'Photos cannot be set as required if Photos section visibility is off.'
      : null,
    disabled: !photoSectionEnabled,
  }),
];
